import { ApolloLink } from '@apollo/client/core';
import { setContext } from '@apollo/client/link/context';

import { FeatureToggleService, TokenService, X_CSRF_TOKEN_HEADER_NAME } from '@hdms/shared/util-core';

import { AUTHORIZATION_HEADER_NAME, CCMSV1, CCMSV2, COS, GQL_DATASOURCE_VERSION_HEADER } from '../constants';
import { CCMSV2_PREFIX } from '../helper';
import { FT_HDMS_CCMSV2 } from '../toggles';

function createHttpHeaders(
  { authToken, csrfToken }: TokenService,
  headers = {},
  addCCMSV2Header = false
): Record<string, string> {
  const httpHeaders = {};

  if (authToken) {
    // Add authorization header when directly accessing gql endpoint.
    httpHeaders[AUTHORIZATION_HEADER_NAME] = authToken;
  } else if (csrfToken) {
    // Add the csrfToken to the headers if in Hybris.
    httpHeaders[X_CSRF_TOKEN_HEADER_NAME] = csrfToken;
  }

  // temporary workaround for adding a `CCMSv2` header
  // TODO: to be removed once finally fully migrated to CCMSv2
  const dataSourceVersionHeader = headers[GQL_DATASOURCE_VERSION_HEADER];

  // suppress adding ccmsv2 header in case ccmsv1 is provided
  if (dataSourceVersionHeader === CCMSV1) {
    delete headers[GQL_DATASOURCE_VERSION_HEADER];
    addCCMSV2Header = false;
  }

  if (addCCMSV2Header && !dataSourceVersionHeader?.includes(CCMSV2) && !dataSourceVersionHeader?.includes(COS)) {
    httpHeaders[GQL_DATASOURCE_VERSION_HEADER] = dataSourceVersionHeader
      ? [CCMSV2].concat(dataSourceVersionHeader)
      : CCMSV2;
  }

  return {
    ...headers,
    ...httpHeaders
  };
}

/**
 * Headers link will update headers per each call, which is required
 * to always get latest token values in case tokens have been updated.
 * @param tokenService
 * @param ftService
 * @return ApolloLink to be added to the link chain
 */
export function createHeadersLink(tokenService: TokenService, ftService: FeatureToggleService): ApolloLink {
  return setContext((request, { headers }) => ({
    headers: createHttpHeaders(
      tokenService,
      headers,
      ftService.getToggleById(FT_HDMS_CCMSV2) || request.operationName?.startsWith(CCMSV2_PREFIX)
    )
  }));
}
